// @ts-strict-ignore
import { useMemo } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { type ComboboxItem, type ComboboxProps, type InputProps } from '@node-space/ui-components'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useAllowedCountriesQuery } from 'hooks/queries/useAllowedCountriesQuery'
import { getCountryOptions } from 'utils/forms'
import {
  BUSINESS_INTENDED_USE_OF_ACCOUNT,
  BUSINESS_SOURCE_OF_FUNDS,
  BUSINESS_USE_CASES,
  COMBOBOX_KEYS,
  ENTITY_TYPES,
  MONTHLY_EXPECTED_VOLUMES,
  RADIO_KEYS,
  SINGLE_DATE_KEYS,
} from '../helpers/constants'
import { useAddBusinessCustomerFormSchema } from '../helpers/schema'
import {
  type AddBusinessCustomerV2ComboboxKey,
  type AddBusinessCustomerV2FormData,
  type AddBusinessCustomerV2FormFields,
  type AddBusinessCustomerV2FormKey,
  type AddBusinessCustomerV2InputKey,
  type AddBusinessCustomerV2SingleDateKey,
} from '../helpers/types'
import { booleanDisplay, findLabel, getOptions } from '../utils/utils'

export const useAddBusinessCustomerForm = (defaultValues: AddBusinessCustomerV2FormData) => {
  const { t } = useTranslation()
  const {
    data: countries = [],
    isFetching: isFetchingCountries,
    isError: isErrorFetchingCountries,
  } = useAllowedCountriesQuery()

  const countryOptions = useMemo(() => getCountryOptions(countries), [countries])

  const useCaseOptions = useMemo(
    () => getOptions(BUSINESS_USE_CASES, 'customers.useCaseOptions', t),
    [t]
  )

  const entityTypeOptions = useMemo(
    () => getOptions(ENTITY_TYPES, 'customers.entityTypeOptions', t),
    [t]
  )

  const monthlyVolumeOptions = useMemo(
    () => getOptions(MONTHLY_EXPECTED_VOLUMES, 'customers.monthlyExpectedVolumeOptions', t),
    [t]
  )

  const sourceOfFundsOptions = useMemo(
    () => getOptions(BUSINESS_SOURCE_OF_FUNDS, 'customers.sourceOfFundsOptions', t),
    [t]
  )

  const intendedUseOptions = useMemo(
    () => getOptions(BUSINESS_INTENDED_USE_OF_ACCOUNT, 'customers.intendedUseOfAccountOptions', t),
    [t]
  )

  const formSchema = useAddBusinessCustomerFormSchema(t)

  const form = useForm<AddBusinessCustomerV2FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })

  const { handleSubmit, setValue, control } = form
  const formValues = useWatch({ control })
  const hasErrors = !!Object.keys(form.formState.errors).length
  const showOperationalAddress = formValues.isOperationalAddressDifferent === 'true'
  const showRegulatorFields = formValues.isRegulated === 'true'

  const { setInputField, setComboboxField, setSingleDateField } =
    useFormFields<AddBusinessCustomerV2FormKey>(formSchema, form, formValues)

  const comboboxItems: Record<AddBusinessCustomerV2ComboboxKey, ComboboxItem[]> = {
    useCase: useCaseOptions,
    entityType: entityTypeOptions,
    taxResidenceCountryCode: countryOptions,
    monthlyExpectedVolumes: monthlyVolumeOptions,
    sourceOfFunds: sourceOfFundsOptions,
    intendedUseOfAccount: intendedUseOptions,
    registeredAddressCountryCode: countryOptions,
    operationalAddressCountryCode: countryOptions,
  }

  const isComboboxLoading: Partial<Record<AddBusinessCustomerV2ComboboxKey, boolean>> = {
    taxResidenceCountryCode: isFetchingCountries,
    registeredAddressCountryCode: isFetchingCountries,
    operationalAddressCountryCode: isFetchingCountries,
  }

  const inputKeys = (Object.keys(defaultValues) as AddBusinessCustomerV2FormKey[]).filter(
    key =>
      !COMBOBOX_KEYS.includes(key as AddBusinessCustomerV2ComboboxKey) &&
      !RADIO_KEYS.includes(key) &&
      !SINGLE_DATE_KEYS.includes(key as AddBusinessCustomerV2SingleDateKey)
  ) as AddBusinessCustomerV2InputKey[]

  const inputFields = inputKeys.reduce(
    (acc, key) => {
      acc[key] = setInputField({
        key,
        ...(key === 'name' && {
          placeholder: t('embeddedCustomers.businessFormPlaceholder.businessName'),
        }),
      })
      return acc
    },
    {} as Record<AddBusinessCustomerV2InputKey, InputProps>
  )

  const comboboxFields = COMBOBOX_KEYS.reduce(
    (acc, key) => {
      acc[key] = {
        ...(setComboboxField({ key, items: comboboxItems[key] }) as ComboboxProps),
        loadingInline: isComboboxLoading[key],
        defaultValue: formValues[key],
      }
      return acc
    },
    {} as Record<AddBusinessCustomerV2ComboboxKey, ComboboxProps>
  )

  const singleDateFields = SINGLE_DATE_KEYS.reduce(
    (acc, key) => {
      const field = setSingleDateField(key)
      acc[key] = {
        ...field,
        labels: {
          ...field.labels,
          date: formSchema?.fields?.[key]?.spec?.label,
        },
      }
      return acc
    },
    {} as Record<AddBusinessCustomerV2SingleDateKey, ReturnType<typeof setSingleDateField>>
  )

  const formFields: AddBusinessCustomerV2FormFields = {
    ...inputFields,
    ...comboboxFields,
  }

  const displayData: Partial<AddBusinessCustomerV2FormData> = {
    useCase: findLabel(useCaseOptions, formValues.useCase),
    entityType: findLabel(entityTypeOptions, formValues.entityType),
    taxResidenceCountryCode: findLabel(countryOptions, formValues.taxResidenceCountryCode),
    monthlyExpectedVolumes: findLabel(monthlyVolumeOptions, formValues.monthlyExpectedVolumes),
    sourceOfFunds: findLabel(sourceOfFundsOptions, formValues.sourceOfFunds),
    intendedUseOfAccount: findLabel(intendedUseOptions, formValues.intendedUseOfAccount),
    isRegulated: booleanDisplay(formValues.isRegulated, t('yes'), t('no')),
    isOperationalAddressDifferent: booleanDisplay(
      formValues.isOperationalAddressDifferent,
      t('yes'),
      t('no')
    ),
    registeredAddressCountryCode: findLabel(
      countryOptions,
      formValues.registeredAddressCountryCode
    ),
    operationalAddressCountryCode: findLabel(
      countryOptions,
      formValues.operationalAddressCountryCode
    ),
  }

  const isErrorLoadingOptions: Partial<Record<AddBusinessCustomerV2FormKey, boolean>> = {
    taxResidenceCountryCode: isErrorFetchingCountries,
    registeredAddressCountryCode: isErrorFetchingCountries,
    operationalAddressCountryCode: isErrorFetchingCountries,
  }

  return {
    form,
    formValues,
    formFields,
    singleDateFields,
    displayData,
    hasErrors,
    handleSubmit,
    setValue,
    isErrorLoadingOptions,
    showOperationalAddress,
    showRegulatorFields,
  }
}
